// uiarc: tooltip (https://uiarc.dev/r/tooltip.json) — Base UI + Tailwind'e çevrildi.
// İlk ipucu 250 ms bekler, tetikleyiciden birkaç piksel yükselir; art arda gelenler (Base UI `timeout` penceresi,
// `data-instant`) bekleme ve yolculuk olmadan yalnız solar. Metin ipucu açıkken değişirse çapraz geçer ve balon
// yeni metin boyutuna yayla uyar. `prefers-reduced-motion` altında hareket yok.
import * as React from 'react'
import { Tooltip as TooltipPrimitive } from '@base-ui/react/tooltip'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

const { blur, duration, ease } = motionTokens

function TooltipProvider({
  delay = 250,
  timeout = 300,
  ...props
}: TooltipPrimitive.Provider.Props) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delay}
      timeout={timeout}
      {...props}
    />
  )
}

function Tooltip({ ...props }: TooltipPrimitive.Root.Props) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger({ ...props }: TooltipPrimitive.Trigger.Props) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

/** Metin değişince çapraz geçer; balon yeni metin boyutuna yayla uyar (ilk ölçüm ve yazı tipi geçişi anında oturur). */
function TooltipText({ text }: { text: string }) {
  const reduced = useReducedMotion()
  const measure = React.useRef<HTMLSpanElement>(null)
  const measured = React.useRef<string | null>(null)
  const [size, setSize] = React.useState<{
    width: number
    height: number
    animate: boolean
  } | null>(null)
  React.useLayoutEffect(() => {
    const node = measure.current
    if (!node) return
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const box = entry.borderBoxSize?.[0]
      const current = node.textContent
      const animate = measured.current !== null && measured.current !== current
      measured.current = current
      setSize({
        width: Math.ceil(box?.inlineSize ?? node.offsetWidth),
        height: Math.ceil(box?.blockSize ?? node.offsetHeight),
        animate,
      })
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  return (
    <motion.span
      className="relative block overflow-clip [overflow-clip-margin:0.5rem]"
      initial={false}
      animate={size ? { width: size.width, height: size.height } : undefined}
      transition={size?.animate && !reduced ? motionTokens.spring.morph : { duration: 0 }}
    >
      <span
        ref={measure}
        className="pointer-events-none invisible absolute top-0 left-0 w-max max-w-[calc(15rem-2*1rem-2px)]"
        aria-hidden="true"
      >
        {text}
      </span>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={text}
          className="block w-max max-w-[calc(15rem-2*1rem-2px)]"
          initial={reduced ? false : { opacity: 0, y: '0.3em', filter: `blur(${blur.soft}px)` }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          exit={
            reduced
              ? { opacity: 0, transition: { duration: 0 } }
              : {
                  opacity: 0,
                  y: '-0.3em',
                  filter: `blur(${blur.subtle}px)`,
                  transition: { duration: duration.instant, ease: ease.standard },
                }
          }
          transition={{ duration: duration.standard, ease: ease.enter }}
        >
          {text}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  )
}

function TooltipContent({
  className,
  side = 'top',
  sideOffset = 8,
  align = 'center',
  alignOffset = 0,
  collisionPadding = 12,
  children,
  ...props
}: TooltipPrimitive.Popup.Props &
  Pick<
    TooltipPrimitive.Positioner.Props,
    'align' | 'alignOffset' | 'side' | 'sideOffset' | 'collisionPadding'
  >) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className="isolate z-90"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            'pop-motion bg-foreground text-background shadow-raised rounded-control leading-body inline-flex w-fit max-w-60 items-center gap-1.5 border border-[color-mix(in_oklab,var(--background)_14%,var(--foreground))] px-4 py-3 text-sm font-normal',
            '[--pop-ease:var(--ease-enter)] [--pop-exit:var(--duration-instant)] [--pop-move:var(--duration-fast)] [--pop-offset:3px]',
            // Art arda gelen ipucu: yolculuk ve bekleme yok, yalnız hızlı solma.
            'data-instant:[--pop-ease:var(--ease-standard)] data-instant:[--pop-fade:var(--duration-instant)] data-instant:[--pop-move:var(--duration-instant)] data-instant:data-starting-style:transform-none',
            className,
          )}
          {...props}
        >
          {typeof children === 'string' || typeof children === 'number' ? (
            <TooltipText text={String(children)} />
          ) : (
            children
          )}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
